import React from 'react';
import {StyleProp, TextStyle} from 'react-native';
import {Button, Text, View} from '@lookiero/aurora';
import {useIntl} from 'react-intl';
import {MountableModal} from '@components/molecules';
import {BoxPreviewStatus} from '@modules/box/core/projection/boxPreview/boxPreview';
import {OrderStatus} from '@domain/model/Order';
import {Step, StepPosition} from './step/Step';
import {StepVariant} from './step/StepVariant';
import {TEXT} from './ModalStepper.definition';
import {style} from './ModalStepper.style';
import {getActiveStep} from '../stepper';
import {CheckoutProjection} from '@lookiero/checkout/dist/src/projection/checkout/checkout';

interface TitleAndDescriptionStepProps {
  readonly title: string;
  readonly description: string;
  readonly stepStyle: StyleProp<TextStyle>;
}

const TitleAndDescriptionStep: React.FC<TitleAndDescriptionStepProps> = ({title, description, stepStyle}) => {
  return (
    <View style={style.titleAndDescriptionWrapper}>
      <Text level={3} style={stepStyle}>
        {title}
      </Text>
      <Text detail level={2} style={stepStyle}>
        {description}
      </Text>
    </View>
  );
};

interface ModalStepperProps {
  readonly isVisible: boolean;
  readonly onClose: () => void;
  readonly checkout: CheckoutProjection | undefined;
  readonly orderStatus: keyof typeof OrderStatus;
  readonly boxPreviewStatus: BoxPreviewStatus | undefined;
}

export const ModalStepper: React.FC<ModalStepperProps> = ({
  isVisible,
  onClose,
  checkout,
  orderStatus,
  boxPreviewStatus,
}) => {
  const {formatMessage} = useIntl();

  const activeStep = getActiveStep({checkout, orderStatus});

  const getStepVariantForStepNumber = (stepNumber: number): StepVariant => {
    if (activeStep > stepNumber) {
      return StepVariant.CHECKED;
    }
    if (activeStep < stepNumber) {
      return StepVariant.DISABLED;
    }

    return StepVariant.DEFAULT;
  };

  return (
    <MountableModal isVisible={isVisible} onClose={onClose} portalID="modal-stepper" testID="modal-stepper">
      <Step variant={StepVariant.CHECKED} position={StepPosition.FIRST}>
        {stepStyle => (
          <TitleAndDescriptionStep
            title={formatMessage(TEXT.STEP_1_TITLE)}
            description={formatMessage(TEXT.STEP_1_DESCRIPTION)}
            stepStyle={stepStyle}
          />
        )}
      </Step>
      <Step variant={getStepVariantForStepNumber(1)} position={StepPosition.DEFAULT}>
        {stepStyle => (
          <TitleAndDescriptionStep
            stepStyle={stepStyle}
            title={formatMessage(TEXT.STEP_2_TITLE)}
            description={formatMessage(TEXT.STEP_2_DESCRIPTION)}
          />
        )}
      </Step>
      <Step variant={getStepVariantForStepNumber(2)} position={StepPosition.DEFAULT}>
        {stepStyle => {
          let title = undefined;
          let description = undefined;

          switch (boxPreviewStatus) {
            case BoxPreviewStatus.NOTIFIED:
              title = TEXT.STEP_3_TITLE_PREVIEW_NOTIFIED;
              description = TEXT.STEP_3_DESCRIPTION_PREVIEW_NOTIFIED;
              break;
            case BoxPreviewStatus.SUBMITTED:
              title = TEXT.STEP_3_TITLE_PREVIEW_SUBMITTED;
              description = TEXT.STEP_3_DESCRIPTION_PREVIEW_SUBMITTED;
              break;
            case BoxPreviewStatus.EXPIRED:
              title = TEXT.STEP_3_TITLE_PREVIEW_EXPIRED;
              description = TEXT.STEP_3_DESCRIPTION_PREVIEW_EXPIRED;
              break;
            default:
              title = TEXT.STEP_3_TITLE_PREVIEW_SEND;
              description = TEXT.STEP_3_DESCRIPTION_PREVIEW_SEND;
          }

          return (
            <TitleAndDescriptionStep
              stepStyle={stepStyle}
              title={formatMessage(title)}
              description={formatMessage(description)}
            />
          );
        }}
      </Step>
      <Step variant={getStepVariantForStepNumber(3)} position={StepPosition.DEFAULT}>
        {stepStyle => (
          <TitleAndDescriptionStep
            stepStyle={stepStyle}
            title={formatMessage(TEXT.STEP_4_TITLE)}
            description={formatMessage(TEXT.STEP_4_DESCRIPTION)}
          />
        )}
      </Step>
      <Step variant={getStepVariantForStepNumber(4)} position={StepPosition.LAST}>
        {stepStyle => (
          <TitleAndDescriptionStep
            stepStyle={stepStyle}
            title={formatMessage(TEXT.STEP_5_TITLE)}
            description={formatMessage(TEXT.STEP_5_DESCRIPTION)}
          />
        )}
      </Step>
      <Button style={style.cta} onPress={onClose}>
        {formatMessage(TEXT.CTA)}
      </Button>
    </MountableModal>
  );
};
